/**
 * media.ts — Centralised R2 media URL helpers.
 * All static assets (images, video) served via the worker /media route → R2.
 * Never reference /public directly.
 */

const API = import.meta.env.VITE_API_URL || 'https://api.fastingcarnivore.com';

/** Build a full URL to an R2-hosted media asset. */
export function mediaUrl(path: string): string {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const cleaned = path.replace(/^\/+/, '').replace(/^media\/+/, '');
  const encoded = cleaned.includes('%') ? cleaned : encodeURI(cleaned);
  return `${API}/media/${encoded}`;
}

// ── Book cover images ──────────────────────────────────────────────────────
export const COVERS = {
  carnivore:   mediaUrl('images/carnivore-cover.jpg'),
  nanoFasting: mediaUrl('images/nano-fasting-cover.jpg'),
  performance: mediaUrl('images/performance-cover.jpg'),
  kitchen:     mediaUrl('images/kitchen-cover.jpg'),
  default:     mediaUrl('images/book-cover.jpg'),
} as const;

/** Return the cover URL for a book slug, falling back to the default. */
export function coverForSlug(slug: string | undefined): string {
  switch (slug) {
    case 'fasting-carnivore':              return COVERS.carnivore;
    case 'nano-fasting':                   return COVERS.nanoFasting;
    case 'fasting-carnivore-performance':  return COVERS.performance;
    case 'fasting-carnivore-kitchen':      return COVERS.kitchen;
    default:                               return COVERS.default;
  }
}

// ── Audio (TTS narration) ─────────────────────────────────────────────────
/** Build a full URL to a chapter audio file in R2. */
export function audioUrl(r2Key: string): string {
  return mediaUrl(r2Key);
}

// ── Video ──────────────────────────────────────────────────────────────────
// Version query bumps phone caches to the Android-safe (H.264 ≤ L4.1) re-encodes.
const VIDEO_VERSION = 'android-h264';
const videoUrl = (path: string): string => `${mediaUrl(path)}?v=${VIDEO_VERSION}`;

export const VIDEOS = {
  loginBg:    videoUrl('videos/login-bg.mp4'),
  meatHero:   videoUrl('meat hero.mp4'),
  meatSteak:  videoUrl('meat steak.mp4'),
  meatGrill:  videoUrl('meat grill.mp4'),
  meatScience: videoUrl('meat science.mp4'),
  meatRibs:   videoUrl('meat ribs.mp4'),
} as const;
